import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  extractAssetGroupId,
  type AssetMetadataV2Lookup,
} from '@infinex/asset-config';
import type { GetAssetMetadataBatchOutput } from '@infinex/main-service-contract/v2/client/assets';

import { getTRPCClient } from '../../config';

// Asset metadata is backend-cached in Redis for 4 hours and refreshed every
// 15 min by the `refreshOfficialAssetsMetadata` Inngest job. Treat it as
// near-static on the client: long staleTime, no refetch on focus.
const ASSET_METADATA_STALE_TIME_MS = 5 * 60 * 1000;

// Keyed by `makeCacheKey(lookup)` from `@infinex/asset-config`.
export type AssetMetadataV2BatchQueryData = GetAssetMetadataBatchOutput;

export function useAssetMetadataV2BatchQuery(lookups: AssetMetadataV2Lookup[]) {
  const sanitizedLookups = useMemo(
    () => lookups.map((lookup) => extractAssetGroupId(lookup)),
    [lookups]
  );

  return useQuery(
    getTRPCClient().assets.getAssetMetadataBatch.queryOptions(
      { lookups: sanitizedLookups },
      {
        enabled: sanitizedLookups.length > 0,
        staleTime: ASSET_METADATA_STALE_TIME_MS,
        refetchOnWindowFocus: false,
        // Keep the prior batch served while a new lookup set is fetching, so
        // consumers don't see a transient `data === undefined` window between
        // balance updates and the next batch resolving.
        placeholderData: (previousData) => previousData,
      }
    )
  );
}
